/** @module csi/widget/_svg/util */
define([
	"dojo/_base/lang",
	"dojo/Deferred",
	"dojo/on",
	"dojo/sniff",
	"dojo/keys",
	"dojo/query",
	"dojo/topic",
	"dojo/when",
	"underscore",
	"canvas/main",
	"canvas/store/reducers/util",
	"csi",
	"csi/api",
	"csi/card/_base",
	"csi/core/ExtendedPath",
	"csi/heading/_base",
	"csi/html",
	"csi/locale",
	"csi/message",
	"csi/core/Key",
	"csi/reflection",
	"csi/service/fontService",
	"csi/siteDefaultLocale",
	"csi/types",
	"csi/util",
	"csi/utility/fontUtility",
	"csi/core/SpecBuilder",
	"csi/widget/MessageDialog",
	"csi/xml"
], function(lang, Deferred, on, sniff, keys, query, topic, when, _, c8Canvas, {addAttributeToObject, restoreDefaultPlaceholderView}, csi, api, csiCard, ExtendedPath,
	csiHeading, html, locale, message, Key, reflection, fontService, siteDefaultLocale, types, util, fontUtility, SpecBuilder, MessageDialog, xml) {

	// Object: map of image sizes by src
	var _mapImageSizes = {};

	/**
	 * Polyfill for HTMLCanvasElement.toBlob (https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/toBlob)
	 */
	if (!HTMLCanvasElement.prototype.toBlob) {
		Object.defineProperty(HTMLCanvasElement.prototype, 'toBlob', {
			value: function(callback, type, quality) {
				var canvas = this;

				setTimeout(function() {
					var data = canvas.toDataURL(type, quality);
					var data2 = data.split(',')[1];
					var binStr = atob(data2);
					var len = binStr.length;
					var arr = new Uint8Array(len);

					for (var i = 0; i < len; i++) {
						arr[i] = binStr.charCodeAt(i);
					}

					callback(new Blob([arr], {type: type || 'image/png'}));
				});
			}
		});
	}

	/**
	 * Create CanvasApp instance
	 * @param {Object} params
	 * @param {Object} listeners
	 */
	function createCanvasApp(params, listeners) {
		params.browserType = {
			isChrome: Boolean(sniff("chrome")),
			isFireFox: Boolean(sniff("ff")),
			isSafari: Boolean(sniff("safari"))
		};

		var canvasApp = new c8Canvas.CanvasApp(params);
		var token = canvasApp.setCanvasListeners(listeners);
		canvasApp.api.page.addPage({pageObjects: []});
		canvasApp.api.canvas.setCanvasContainer(params.editorParentContainer);

		// Register c8 function with c8-canvas app so it can be called from canvas app
		canvasApp.setC8Functions({
			getDisplayNameForKey: this.getDisplayNameForKey,
			getDisplayNameForType: this.getDisplayNameForType,
			getStartTypeForKey: this.getStartTypeForKey,
			localizeString: this.localizeString,
			displayCustomView: this.displayCustomView,
			displayTypeInstances: this.displayTypeInstances,
		});

		// store listener token with canvasApp to be removed later
		// TODO: maybe enhance CanvasApp class to hold this token, or possibly all tokens held by listeners
		canvasApp.__token__ = token;

		return canvasApp;
	}

	/**
	 * Stop using instance of CanvasApp, which basically means any listeners need to be removed
	 * @param {CanvasApp} canvasApp
	 */
	function removeCanvasApp(canvasApp) {
		canvasApp.removeCanvasListeners(canvasApp.__token__);
	}

	/**
	 * When the dontDownloadImages parameter is used when creating the CanvasApp instance, then image href attributes
	 * are replaced with a custom attribute so that images are not downloaded as a result. This function is then necessary
	 * to convert the href attributes in the given HTML content back to what they should be.
	 * @param {string} htmlContent
	 * @return {string}
	 */
	function resolveCanvasAppImageHref(htmlContent) {
		// FIX: eventually there should be a canvas API to combine createCanvasApp, removeCanvasApp, and this function all in one
		// to convert ContentJson to HTML without downloading images
		return htmlContent.replace(/data-csi-href/g, "xlink:href");
	}

	/**
	 * Set CanvasApp page content
	 * @param {CanvasApp} canvasApp
	 * @param {CanvasContent} content
	 * @param {boolean} [bPreserveSelection]
	 */
	function setCanvasAppContent(canvasApp, content, bPreserveSelection) {
		var pageModel = content.data ? JSON.parse(content.data) : {pageObjects: []};

		// set page id in the page model. Due to copy/paste there could be difference in id.
		pageModel.id = content.id;

		if (!_.isEmpty(content.keys)) {
			// call canvas API to substitute pageModel with placeholder keys, for example when load canvas
			content.data = canvasApp.pageHelper.setPagePlaceholderKeys(pageModel, content.keys);
			content.keys = null;
		}
		canvasApp.api.page.setPages([pageModel], bPreserveSelection);
	}

	return {
		// String: type of DnD item accepted by canvas
		DND_TYPE_IMAGE: "image",
		DND_TYPE_NODE: "node",



		// String[]: attributes used for box
		boxAttrs: ["x", "y", "width", "height"],

		// String[]: shape attributes used for copy formatting
		shapeAttrs: ["fill", "stroke", "stroke-dasharray", "stroke-width"],

		// Object: Canvas Mode enum get from c8-canvas
		canvasModes: null,

		// Object: Shape properties
		shapeProperties: {
			arrow: "spPr.ln",
			autosize: "spPr.autosize",
			fillColor: "fillColor",
			imagePlaceholderShape: "spPr.geometry.presetShape",
			strokeColor: "strokeColor",
			strokeDash: "spPr.ln.dash",
			strokeWidth: "spPr.ln.W",
			verticalAlign: "spPr.verticalAlign",
		},
		// Number: Froala popup shift offset
		froalaPopUpOffset: 165,

		// Object[]: default styles used for text in froala editor
		defaultStyles: [
			{style: 'color', value: 'color'},
			{style: 'fontFamily', value: 'font-family'},
			{style: 'textAlign', value: 'text-align'},
			{style: 'fontSize', value: 'font-size'},
			{style: 'marginLeft', value: 'margin-left'}
		],

		// Object[]: default tag styles used for text in froala editor
		tagStyles: [
			{style: 'strong', value: 'strong'},
			{style: 'OL', value: 'ol'},
			{style: 'UL', value: 'ul'},
			{style: 'underline', value: 'u'},
			{style: 'italic', value: 'em'},
			{style: 'strikethrough', value: 's'},
			{style: 'paragraph', value: 'h1'},
			{style: 'paragraph', value: 'h2'},
			{style: 'paragraph', value: 'h3'},
			{style: 'paragraph', value: 'h4'}
		],

		/** @property {String[]} alignStyles - default text align options */
		alignStyles: ['left', 'center', 'right'],

		/** Shape types enum */
		shapeTypes: {
			SHAPE: "SHAPE",
			PICTURE: "PICTURE",
			GROUP_SHAPE: "GROUP_SHAPE",
			CONNECTION_SHAPE: "CONNECTION_SHAPE"
		},

		// Number: default grid to snap to
		gridInc: 10,

		// String[]: attributes used for lines
		lineAttrs: ["x1", "y1", "x2", "y2", "stroke", "stroke-dasharray", "stroke-width", "marker-start", "marker-end"],

		// Object: map of namespace prefix to namespace
		namespaces: {
			xmlns: "http://www.w3.org/2000/xmlns/",
			svg: "http://www.w3.org/2000/svg",
			xlink: "http://www.w3.org/1999/xlink",
			xml: "http://www.w3.org/XML/1998/namespace"
		},

		// Number: ratio used to map canvas pixel size to SVG space
		pixelToSvgRatio: 1,

		// Number: current zoom factor
		zoomFactor: 1,

		// Object: mapping of shape tag to Shape class
		shapes: {},

		// Object: attributes available for shape nodes
		shapeStyles: {
			arrow: "0",
			fill: "#e6e6fa",
			stroke: "#000000",
			"stroke-dasharray": "none",
			"stroke-width": 2
		},

		shapeTextStyles: {
			arrow: "0",
			fill: "none",
			stroke: "none",
			"stroke-dasharray": "none",
			"stroke-width": 3
		},

		shapeTextDragStyles: {
			fill: "#ffffff",
			stroke: "#0784B6",
			"stroke-width": 1,
			"stroke-dasharray": "none"
		},

		// Object: attributes used for selection
		dragSelectStyles: {
			fill: "#0784B6",
			"fill-opacity": 0.1,
			stroke: "#0784B6",
			"stroke-width": "z_2.4"
		},

		selectStyles: {
			fill: "none",
			stroke: "blue",
			"stroke-width": "z_0.6"
		},

		lockStyles: {
			fill: "none",
			stroke: "red",
			"stroke-width": "z_0.6"
		},

		shapeMouseEnter: {
			fill: "none",
			stroke: "#0784B6",
			"stroke-width": "z_1.5"
		},

		circleStyles: {
			r: "z_3",
			fill: "white",
			stroke: "blue",
			"stroke-width": "z_1.2"
		},

		sideStyles: {
			width: "z_6",
			height: "z_6",
			fill: "white",
			stroke: "blue",
			"stroke-width": "z_1.2"
		},

		rotateStyles: {
			r: "z_3",
			fill: "#7fff00",
			stroke: "blue",
			"stroke-width": "z_1.2"
		},

		// Object: attributes for displaying image clip region
		clipPathStyles: {
			fill: "none",
			stroke: "#0784B6",
			"stroke-width": "z_2.4"
		},

		// Object: styles for drag shape
		cropStyles: {
			fill: "none",
			stroke: "black",
			"stroke-width": "z_2.4"
		},

		// Object: pick path styles
		pickPathStyles: {
			fill: "none",
			stroke: "#ffffff",
			"stroke-opacity": 0.01,
			"stroke-width": "z_18"
		},

		// Used to identify whether object is locked or unlocked
		// specially used while selection of object to decide whether to apply red(locked) or blue(not locked) style
		lock: false,

		// String[]: dash array styles by index
		dashStyles: ["SOLID", "SMALL_DOT", "DOT", "DASH", "MEDIUM_DASH", "LARGE_DASH_DOT", "LARGE_DASH"],

		// Object[]: arrow types by index
		arrowTypes: [
			{headEnd: "NO_ARROW", tailEnd: "NO_ARROW"},
			{headEnd: "NO_ARROW", tailEnd: "LINE_ARROW"},
			{headEnd: "LINE_ARROW", tailEnd: "NO_ARROW"},
			{headEnd: "LINE_ARROW", tailEnd: "LINE_ARROW"},
			{headEnd: "NO_ARROW", tailEnd: "TRIANGLE_ARROW"},
			{headEnd: "TRIANGLE_ARROW", tailEnd: "NO_ARROW"},
			{headEnd: "TRIANGLE_ARROW", tailEnd: "TRIANGLE_ARROW"},
			{headEnd: "NO_ARROW", tailEnd: "STEALTH_ARROW"},
			{headEnd: "STEALTH_ARROW", tailEnd: "NO_ARROW"},
			{headEnd: "STEALTH_ARROW", tailEnd: "STEALTH_ARROW"}
		],

		// Object: Text style map
		oldTextStyleMap: {
			middle: 'center',
			start: 'left',
			end: 'right'
		},

		// String[]: stroke width styles by index
		widthStyles: ["1", "2", "3", "4", "5"],

		// SVGNode: placeholder for SVG utility functions
		_svgTemp: null,

		// Object: precision to truncate various transforms and attributes
		_trunc: {
			rotate: 1,
			scale: 3,
			translate: 1,
			width: 1,
			height: 1,
			cx: 1,
			cy: 1,
			rx: 1,
			ry: 1,
			r: 1,
			x: 1,
			y: 1,
			x1: 1,
			y1: 1,
			x2: 1,
			y2: 1
		},

		// String: prefix used on styles to indicate whether or not they are affected by zoom factor
		_zoomPrefix: "z_",

		createCanvasApp: createCanvasApp,
		removeCanvasApp: removeCanvasApp,
		resolveCanvasAppImageHref: resolveCanvasAppImageHref,
		setCanvasAppContent: setCanvasAppContent,

		initialize: function(/*SVGNode*/svgTemp) {
			// summary: store reference to SVG temporary node - currently only used for text wrapping functionality
			this._svgTemp = svgTemp;
		},

		createShape: function(/*String*/tag, /*Object?*/params) {
			return new this.shapes[tag](params);
		},

		getShapeForNode: function(/*SVGNode*/node) {
			// summary: create shape for node - the tag is gotten by looking for the first child
			// repeatedly until a non-group node is found (such as rect, image, etc.)
			var svg = node;
			var tag;

			if (svg.attributes["group"]) {
				tag = "g";
			} else {
				while (svg.firstChild) {
					svg = svg.firstChild;
					tag = svg.tagName;
				}
			}

			return this.createShape(tag, {svgGroup: node});
		},

		registerShape: function(/*String*/tag, /*Function*/shapeCtr) {
			this.shapes[tag] = shapeCtr;
		},

		isText: function(/*SVGNode*/svg) {
			return svg.getAttributes(["data-csi-shape-type"])["data-csi-shape-type"] == "text";
		},

		compareSvg: function(/*String*/svg1, /*String*/svg2) {
			// summary: return true if two svg strings are the same
			var temp1 = document.createElement('div');
			var temp2 = document.createElement('div');

			// trying to add svg content using .html() wont work as expected in ie
			temp1.innerHTML = svg1;
			temp2.innerHTML = svg2;
			$(temp1).find('*').removeAttr('id');
			$(temp2).find('*').removeAttr('id');

			return $(temp1).html() == $(temp2).html();
		},

		degToRad: function(/*Number*/d) {
			// summary: convert degrees to radians
			return d * Math.PI / 180;
		},

		radToDeg: function(/*Number*/r) {
			// summary: convert radians to degrees
			return r * 180 / Math.PI;
		},

		find: function(/*Array*/arr, /*Function|String*/callback, /*Object?*/thisObject) {
			// summary: return object that matches callback in array
			var x;

			if (!arr) return x;

			_.some(arr, function(item, i, arr) {
				if (callback.call(this, item, i, arr)) {
					x = item;
					return true;
				}

				return false;
			}, thisObject);

			return x;
		},

		generateEmptyPageJson: function(id) {
			return "{\"pageObjects\":[],\"id\":\"" + id + "\",\"contentChanges\":-1}";
		},

		/**
		 * Check whether a type is a canvas container but not a data sheet revision
		 * @param {C8Node} node
		 */
		isNonRevisionCanvas: function(node) {
			const rc = reflection.getNodeClass(node);

			return rc.isDerivedFrom("CanvasContainer") && !rc.isDerivedFrom("DataSheetRevision");
		},

		/**
		 * Get image size.  If it's already been loaded grab it from map, if not grab it from image node,
		 * if neither are available then insert image on the page and grab its size from there
		 * @param {String} src
		 * @param {String} name
		 * @param {Cache} cache
		 * @return {Deferred}
		 */
		imageSize: function(src, name, cache) {
			var dfd = new Deferred();
			var s = _mapImageSizes[src];
			var img;
			const regex = /\?URL\=C[^&]+|centric:[^&]+|cf:[^&]+/g;
			const matches = src.match(regex);

			var onLoad = function() {
				// store size of image to catch browser errors where width/height is sometimes 0 on subsequent loads
				s = {w: img.width, h: img.height};
				_mapImageSizes[src] = s;
				img.parentNode.removeChild(img);
				dfd.resolve(s);
			};

			var onError = function(err) {
				img.parentNode.removeChild(img);
				dfd.reject(new Error(name || src));
			};

			var loadImageForSize = function() {
				img = html.create("img");

				handles = [
					on(img, "load", onLoad),
					on(img, "error", onError),
					on(img, "abort", onError)
				];

				// Chrome automatically rotates images based on image orientation, but seems
				// to only respect css telling it not to if it's on the page, so attaching this
				// image
				img.style.visibility = "hidden";
				document.getElementById("canvas-editor-root").appendChild(img);

				img.src = src;
			}

			if (s) {
				dfd.resolve(s);
			} else if (matches) {
				const centricUrl = matches[0].substring(5);
				var nodeDfd = cache.getNodeDeferred(centricUrl, true);

				when(nodeDfd, function(node) {
					if (node) {
						s = {w: node.Width, h: node.Height};
						_mapImageSizes[src] = s;

						dfd.resolve(s);
					} else {
						loadImageForSize();
					}
				});
			} else {
				loadImageForSize();
			}

			return dfd;
		},

		/**
		 * Return index of child under parent
		 * @param {SVGNode} n
		 */
		nodeIndex: function(n) {
			var i = 0;
			// Check if current selection has textOverflow or not
			// if overflow, we have added control point in g so need to take parent of clicked rect node
			if (this.isTextOverflowAttr(n)) {
				n = n.parentNode;
			}
			while (n = n.previousSibling) ++i;
			return i;
		},

		/**
		 * Check of textOverflow attribute on node
		 * @param  {SVGNode} node
		 * @return {Boolean}
		 */
		isTextOverflowAttr: function(node) {
			return node.getAttribute('textOverflow') || node.nodeName === 'text';
		},

		boxFromPoints: function(/*Point[]*/points) {
			var x1 = 10000;
			var y1 = 10000;
			var x2 = -10000;
			var y2 = -10000;

			_.each(points, function(p) {
				x1 = Math.min(x1, p.x);
				y1 = Math.min(y1, p.y);
				x2 = Math.max(x2, p.x);
				y2 = Math.max(y2, p.y);
			});

			return {
				x: x1,
				y: y1,
				width: x2 - x1,
				height: y2 - y1
			};
		},

		boxCenter: function(/*SVGRect*/b) {
			return {
				x: b.x + b.width / 2,
				y: b.y + b.height / 2
			};
		},

		boxCornerPoints: function(/*SVGRect*/box) {
			// summary: return corner points for box
			var l = box.x;
			var r = box.x + box.width;
			var t = box.y;
			var b = box.y + box.height;
			if (this.lock) {
				return [
					{x: l, y: t},
					{x: r, y: t},
					{x: r - 12, y: t + 6},
					{x: r, y: b},
					{x: l, y: b}
				];
			}
			return [
				{x: l, y: t},
				{x: r, y: t},
				{x: r, y: b},
				{x: l, y: b}
			];
		},

		boxSidePoints: function(/*SVGRect*/b) {
			// summary: return corner and side points for box - starting from left-top and moving clockwise
			var l = b.x;
			var c = b.x + b.width / 2;
			var r = b.x + b.width;
			var t = b.y;
			var m = b.y + b.height / 2;
			var b = b.y + b.height;

			return [
				{x: c, y: t},
				{x: r, y: m},
				{x: c, y: b},
				{x: l, y: m}
			];
		},

		boxScale: function(/*SVGRect*/box, /*Number*/s) {
			// summary: scale box - for now assume scale around (0,0)
			return {
				x: box.x * s,
				y: box.y * s,
				width: box.width * s,
				height: box.height * s
			};
		},

		boxRotate: function(/*SVGRect*/box, /*Number*/rad) {
			// summary: return box surrounding a rotated box (rotation in radians)
			if (!rad) {
				return box;
			}

			var center = this.boxCenter(box);
			var points = this.boxCornerPoints(box);

			points = _.map(points, function(p) {
				return this.pointRotate(p, center, rad);
			}, this);

			return this.boxFromPoints(points);
		},

		boxTranslate: function(/*SVGRect*/box, /*Number[]*/t) {
			// summary: translate box
			return {
				x: box.x + t[0],
				y: box.y + t[1],
				width: box.width,
				height: box.height
			};
		},

		boxContainsBox: function(/*SVGRect*/b1, /*SVGRect*/b2) {
			// summary: return true if b1 fully contains b2
			return (b1.x <= b2.x) && (b1.x + b1.width >= b2.x + b2.width) &&
				(b1.y <= b2.y) && (b1.y + b1.height >= b2.y + b2.height);
		},

		/**
		 * Return point for use node - whether rect or circle
		 * @param  {SVGNode} node
		 */
		point: function(node) {
			var nodeName = node.tagName.toLowerCase();
			if (node.nodeName.toLowerCase() == "rect" || nodeName === 'text') {
				return this.boxCenter(this.svgGetAttributes(node, this.boxAttrs, true));
			}

			return {
				x: Number(node.getAttribute("cx")),
				y: Number(node.getAttribute("cy"))
			};
		},

		/**
		 * Check if given page is empty or not. A page is empty if it has no page objects (shapes).
		 * @param {Object} pageJSON
		 * @returns {boolean} True if page has page objects.
		 */
		isNonEmptyPage: function(pageJSON) {
			return pageJSON && pageJSON.pageObjects && pageJSON.pageObjects.length > 0;
		},

		/**
		 * Compare svg JSON and return true if both are same
		 * @param {Object} oldContent
		 * @param {Object} newContent
		 * @return {boolean}
		 */
		compareJSONContents: function(oldContent, newContent) {
			var contentData;
			var newPageContent;

			if (oldContent) {
				contentData = JSON.parse(oldContent);
				delete contentData.contentChanges;
				delete contentData.id;
			}

			if (newContent) {
				newPageContent = JSON.parse(newContent);
				delete newPageContent.contentChanges;
				delete newPageContent.id;
			}

			return JSON.stringify(contentData) === JSON.stringify(newPageContent);
		},

		pointRotate: function(/*Point*/p, /*Point*/c, /*Number*/rad) {
			// summary: rotate point p around point c by radians rad
			if (!rad) return p;

			var d = this.distance(c, p);
			var r = rad + this.radians(c, p);

			return {
				x: c.x + Math.cos(r) * d,
				y: c.y + Math.sin(r) * d
			};
		},

		distance: function(/*Point*/p1, /*Point*/p2) {
			// summary: compute distance between two points
			return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
		},

		distancePointToLine: function(/*Point*/p, /*Point*/p1, /*Point*/p2) {
			// summary: compute distance from point p to line segment formed by p1 and p2
			var dx = p2.x - p1.x;
			var dy = p2.y - p1.y;
			if (!dx && !dy) {
				return this.distance(p, p1);
			}

			var u = (dx * (p.x - p1.x) + dy * (p.y - p1.y)) / (dx * dx + dy * dy);
			if (u < 0) {
				return this.distance(p, p1);
			} else if (u > 1) {
				return this.distance(p, p2);
			}

			return this.distance(p, {
				x: p1.x + u * dx,
				y: p1.y + u * dy
			});
		},

		equals: function(/*Point*/p1, /*Point*/p2) {
			// summary: compute distance between two points
			return (p1.x == p2.x) && (p1.y == p2.y);
		},

		radians: function(/*Point*/p1, /*Point*/p2) {
			// summary: return the angle formed by two points - since Y coordinates decrease
			// as they go up the page, need to reverse Y direction
			var r = Math.atan2(p2.y - p1.y, p2.x - p1.x);
			return (r < 0) ? 2 * Math.PI + r : r;
		},

		slope: function(/*Point*/p1, /*Point*/p2) {
			// summary: given two points of a line, returns the slope.
			var x = p2.x - p1.x;
			return x ? (p2.y - p1.y / x) : 0;
		},

		snap: function(/*Number*/n, /*Number?*/inc) {
			// summary: snap number to grid
			inc = inc || this.gridInc;
			return Math.round(n / inc) * inc;
		},

		getMarkerOptionsFromUrl: function(/*String*/url,/*Object*/arrowPlugin) {
			// create marker definitions if not existing
			if (url) {
				var mrkIdArray = url.slice(6).slice(0, -1).split("_");
				arrowPlugin.getMarker(mrkIdArray[0], "#" + mrkIdArray[1], mrkIdArray[2]);
			}
		},

		getMarkerUrl: function(/*String*/id) {
			return "url(#" + id + ")";
		},

		getMarkerUniqueId: function(/*Number*/pathIndex, /*String*/color, /*Number*/width) {
			return "A" + pathIndex + "_" + color.replace(/(#|rgb| |,|\(|\))/g, "") + "_" + width;
		},

		svgAttributes: function(/*SVGNode*/node, /*Object*/attrs) {
			// summary: set attributes on given SVG node
			var t = this._trunc;
			var v, ns;

			for (var i in attrs) {
				v = this.zoomAttribute(attrs[i]);

				if (v !== null) {
					// truncate certain attributes to smallest precision before setting
					if (p = t[i]) {
						v = this.truncate(v, p);
					}

					// set attribute
					var splitAttr = i.split(":");
					if (splitAttr.length > 1 && (ns = this.namespaces[splitAttr[0]])) {
						node.setAttributeNS(ns, i, v);
					} else {
						node.setAttribute(i, v);
					}
				}
			}
		},

		svgContainerClean: function(/*String*/s) {
			// accepts serialized svg and cleans up container
			if (s) {
				s = s.replace('viewbox=""', "");

				// need to replace all br
				s = s.replace(/<br>/gi, "<br/>");

				//replace ns1:href to xlink:href for image
				s = s.replace(/NS\d+:href/gi, 'xlink:href');

				// Instead of modifying the rendered DOM creating document fragment and modifying
				// all the id's with suffixing t for thumbnail.
				var svgFragment = document.createDocumentFragment();
				var tempDiv = document.createElement("div");
				svgFragment.appendChild(tempDiv);
				tempDiv.innerHTML = s;

				// We want to remove all div which we want to hide in thumbnail.
				query("div.c8-canvas-hide-in-thumbnail", tempDiv).orphan();

				this.svgUniqueIds($(svgFragment.childNodes[0]));
				s = svgFragment.childNodes[0].innerHTML;
			}

			return s;
		},

		/** Get the Image Spec for a Product Presentation Set Item node.
		 * @param {C8Node} node - Product Presentation Set Item node to get imageSpec for
		 * @return {Spec}
		 */
		getImageSpecForSetItem: function(node) {
			const path = xml.path(xml.isEmptyURL(node.PPIProductColor) ? "PPIProduct" : "PPIProductColor");
			const extendedPath = ExtendedPath.create(node.$Type, path);
			const imageKey = new Key({
				id: reflection.ATTR_IMAGES,
				extendedPath,
			});
			return new SpecBuilder().getSpecForKey(imageKey);
		},

		svgGetAttributes: function(/*SVGNode*/node, /*String[]*/attrs, /*Boolean*/bNumbers) {
			// summary: get attributes from node
			var o = {};

			if (bNumbers) {
				_.each(attrs, function(a) {
					o[a] = Number(node.getAttribute(a));
				});
			} else {
				_.each(attrs, function(a) {
					o[a] = node.getAttribute(a);
				});
			}

			return o;
		},

		/**
		 * Draw circle for curve points
		 * @param  {Object} p
		 * @param  {SVGNode} parent
		 * @param  {SVGNode} before
		 */
		svgPoint: function(p, parent, before) {
			return this.svgNew("circle", lang.mixin({
				cx: p.x,
				cy: p.y
			}, this.circleStyles), parent, before);
		},

		/**
		 * Create selection side box
		 * @param  {Point} p
		 * @param  {_Shape} parent
		 * @param  {_Shape} before
		 * @param  {Boolean} lockIcon
		 * @param  {Boolean} isTextOverflow
		 * @return {SVGNode}
		 */
		svgSidePoint: function(p, parent, before, lockIcon, isTextOverflow) {
			var WIDTH = 12;
			var attrs = lang.clone(this.sideStyles);
			attrs.stroke = this.lock ? "red" : "blue";
			var style;
			// if shape is lock adding lock icon
			if (lockIcon) {
				var node = this.svgNew("foreignObject", lang.mixin({
					x: p.x - this.zoomAttribute(attrs.width) / 2,
					y: p.y - this.zoomAttribute(attrs.height) / 2
				}, attrs), parent);
				node.innerHTML = "<span style='font-size:9px' class='material-icons csi-icon-color-red'>lock</span>";
				return node;
			}
			var outerG;
			if (isTextOverflow) {
				attrs.width = WIDTH;
				attrs.height = WIDTH;
				// adding the control point in g tag if text is overflow
				outerG = this.svgNew("g", {}, parent, before);
				// adding textOverflow attribute to distinguish while transform operation
				attrs['textOverflow'] = true;
			}
			var x = p.x - this.zoomAttribute(attrs.width) / 2;
			var y = p.y - this.zoomAttribute(attrs.height) / 2;
			parent = isTextOverflow ? outerG : parent;
			before = isTextOverflow ? undefined : before;
			var rect = this.svgNew("rect", lang.mixin({
				x: x,
				y: y
			}, attrs), parent, before);
			// Adding + icon if text is overflow
			if (isTextOverflow) {
				var text = this.svgNew("text", {x: x + 2.5, y: y + 10}, parent, before);
				text.textContent = '+';
			}
			return rect;
		},

		svgRotatePoint: function(/*SVGRect*/b, /*SVGNode*/parent, /*SVGNode*/before) {
			// summary: create rotate point
			var x = b.x + b.width / 2;
			var y = b.y - 5 * this.zoomAttribute(this.rotateStyles.r);
			var style = this.lock ? this.lockStyles : this.selectStyles;
			this.svgNew("line", lang.mixin({
				x1: x,
				y1: y,
				x2: x,
				y2: b.y
			}, style), parent, before);
			this.rotateStyles.stroke = this.lock ? "red" : "blue";
			this.rotateStyles.fill = "white";
			return this.svgNew("circle", lang.mixin({
				cx: x,
				cy: y
			}, this.rotateStyles), parent, before);
		},

		svgNew: function(/*String*/tag, /*Object?*/attrs, /*SVGNode?*/parentNode, /*SVGNode?*/beforeNode) {
			// summary: create new SVGNode with optional attributes
			var node = document.createElementNS(this.namespaces.svg, tag);
			if (attrs) {
				this.svgAttributes(node, attrs);
			}

			if (parentNode) {
				if (beforeNode) {
					parentNode.insertBefore(node, beforeNode);
				} else {
					parentNode.appendChild(node);
				}
			}

			return node;
		},

		svgRotate: function(/*SVGNode?*/svg, /*Point*/p, /*Number*/rad) {
			// summary: set rotation on given node given radians - round to nearest degree
			return this.svgTransform(svg, "rotate", [this.radToDeg(rad) % 360, p.x, p.y]);
		},

		svgTransform: function(/*SVGNode?*/svg, /*String*/op, /*Number[]*/params) {
			// summary: apply list of transforms to given node
			var precision = this._trunc[op];

			params = _.map(params, function(p) {
				return this.truncate(p, precision);
			}, this);

			var t = op + "(" + params.join(",") + ")";
			if (svg) svg.setAttribute("transform", t);
			return t;
		},

		svgTransformParams: function(/*SVGNode*/svg) {
			// summary: get transform parameters from SVG node - assumption is transform operation is known
			var t = svg.getAttribute("transform");
			if (!t) return null;

			var n1 = t.indexOf("(") + 1;
			var n2 = t.indexOf(")");
			var s = t.substring(n1, n2);
			var a = s.split(",");

			// IE seems to store attribute without commas, so split by spaces instead
			if (a.length == 1) {
				a = s.split(" ");
			}

			return _.map(a, Number);
		},

		svgUniqueIds: function(/*SVGNode*/svg) {
			// summary: replace all ids except markers with unique id
			// 1. C8-canvas lib always generate unique ids for its elements.
			// 2. Here in the thumbnail, we need to update it again to be unique per html document.
			// Given above two facts, if we simply update the ids by means of suffixing them with some
			// constant value (say '_t', t for thumbnail), the result set would be always unique.
			$(svg).find("[id],[fill],[marker-start],[marker-end]").not("clippath").each(function() {

				var oldId = $(this).attr("id");
				if (oldId) {
					$(this).attr("id", oldId + "_t");
				}
				var oldFill = $(this).attr("fill");
				if (oldFill) {
					$(this).attr("fill", oldFill.replace(/\)/g, "_t)"));
				}
				var oldMarkerStart = $(this).attr("marker-start");
				if (oldMarkerStart) {
					$(this).attr("marker-start", oldMarkerStart.replace(/("?)\)/g, "_t$1)"));
				}
				var oldMarkerEnd = $(this).attr("marker-end");
				if (oldMarkerEnd) {
					$(this).attr("marker-end", oldMarkerEnd.replace(/("?)\)/g, "_t$1)"));
				}

				// I am not sure about clipPath, so keeping it as it is along with old logic
				var clipPath = $(this).find("clipPath");
				if (clipPath.length) {
					var uniqueId = csi.getUniqueId();
					var clipId = "C" + uniqueId.substring(1);
					clipPath.attr("id", clipId);
					$(this).find("image").attr("clip-path", "url(#" + clipId + ")");
				}
			});
		},

		truncate: function(/*Number*/x, /*Number*/precision) {
			// summary: truncate number to given precision
			var s = Math.pow(10, precision);
			return Math.round(Number(x) * s) / s;
		},

		viewBox: function(/*SVGNode|String*/svg) {
			// summary: return view box - make sure to support both SVGRect box (width/height) and Dojo box (w/h)
			var s;

			if (_.isString(svg)) {
				var res = /viewBox=\"(.+?)\"/.exec(svg);
				s = res && res[1];
			} else {
				s = svg.getAttribute("viewBox");
			}

			if (s) {
				var vb = s.split(" ");

				return {
					x: Number(vb[0]),
					y: Number(vb[1]),
					w: Number(vb[2]) - Number(vb[0]),
					h: Number(vb[3]) - Number(vb[1])
				};
			}

			return null;
		},

		/**
		 * Migrate old text data to support the froala in place text editing
		 * Old text has given six styles support so extracting that data and creating HTML node supported by froala
		 * Default markup:  1. Applying text align on parent level
		 * 					2. Creating span tag and applying color and font size
		 * Optional Markup: - It'd depend on bold, italic, underline, if any of this style is there we are create mockup for it.
		 * Example: <text fill="#32cd32" font-size="10px" text-anchor="start" text-decoration="underline"  x="285" y="80">
		 *   			<tspan x="230" y="87">Hello</tspan>
		 *			</text>
		 *       Returns:
		 * 			<p style="text-align: left;">
		 * 				<span style="color: rgb(50,205,50); font-size: 10px;">
		 * 					<u>Hello</u>
		 * 				</span></p>
		 * @param  {SVGNode} svgText
		 * @param  {String} value
		 * @return {SVGNode}
		 */
		migrateOldTextToNewText: function(svgText, value) {
			var textElement = '';
			var spanElement = '';
			var styleElement = '';
			var oldStyles = ['text-anchor', 'fill', 'font-size', 'font-weight', 'font-style', 'text-decoration'];
			var textStyles = this.svgGetAttributes(svgText, oldStyles);

			function pointToPixel(value) {
				return parseInt(value) * 96 / 72;
			}

			function styleCheck(style) {
				return textStyles[style] !== 'normal' && textStyles[style] !== 'none' && textStyles[style];
			}

			function HEXtoRGB(hex) {
				var rgb = html.HEXtoRGB(hex);
				return rgb ? 'rgb(' + rgb[0] + ', ' + rgb[1] + ', ' + rgb[2] + ')' : '';
			}

			function mapStyleValue(value) {
				var oldTextStyleMap = {
					middle: 'center',
					start: 'left',
					end: 'right'
				}
				return oldTextStyleMap[value];
			}

			_.each(oldStyles, function(style) {
				switch (style) {
					case 'text-anchor':
						var mapValue = mapStyleValue(textStyles[style]);
						textElement = document.createElement('p');
						textElement.style.textAlign = mapValue;
						break;
					case 'fill':
						spanElement = document.createElement('span');
						spanElement.style.color = HEXtoRGB(textStyles[style] === 'black' ? '#000000' : textStyles[style]);
						break;
					case 'font-size':
						var size = parseInt(pointToPixel(textStyles[style]));
						spanElement.style.fontSize = size + 'px';
						break;
					case 'font-weight':
						if (styleCheck(style)) {
							styleElement = document.createElement('strong');
						}
						break;
					case 'font-style':
						if (styleCheck(style)) {
							if (styleElement) {
								styleElement.appendChild(document.createElement('em'));
							} else {
								styleElement = document.createElement('em');
							}
						}
						break;
					case 'text-decoration':
						if (styleCheck(style)) {
							if (styleElement && !styleElement.firstChild) {
								styleElement.appendChild(document.createElement('u'));
							} else if (styleElement && styleElement.firstChild) {
								styleElement.firstChild.appendChild(document.createElement('u'));
							} else {
								styleElement = document.createElement('u');
							}
						}
						break;
				}

			}, this);
			// Check if any css property like bold, italic and underline is applied
			// if yes appending text to strong, u or em denpend on styles.
			// if no appeding text to span with font color, font-size
			if (spanElement && !styleElement) {
				spanElement.innerHTML = value;
				textElement.appendChild(spanElement);
			} else {
				if (styleElement && !styleElement.firstChild) {
					styleElement.innerHTML = value;
				} else if (styleElement.firstChild && !styleElement.firstChild.firstChild) {
					styleElement.firstChild.innerHTML = value;
				} else {
					styleElement.firstChild.firstChild.innerHTML = value;
				}
				textElement.appendChild(spanElement);
				textElement.firstChild.appendChild(styleElement);
			}

			return textElement;
		},

		/**
		 * Filter the unlocked shapes
		 * @param  {_Shape} shapes
		 * @return {_Shape[]}
		 */
		filterUnLockedShapes: function(shapes) {
			return _.filter(shapes, function(shape) {return !shape.getShapeLock();});
		},

		/**
		 * Check of text overflow in shape and text box
		 * @param  {Number} scrollHeight
		 * @param  {Number} height
		 * @return {Boolean}
		 */
		isTextOverflow: function(scrollHeight, height) {
			// if scrollHeight is less than 5 as compare to height, in this case text is overflow
			return scrollHeight - 5 > height;
		},

		/**
		 * Convert RGB color to HEX
		 * @param  {String} rgb
		 * @return {String}
		 */
		RGBToHex: function(rgb) {
			function hex(x) {
				return ('0' + parseInt(x, 10).toString(16)).slice(-2);
			}
			try {
				if (!rgb || rgb === 'transparent') return '';

				if (/^#[0-9A-F]{6}$/i.test(rgb)) return rgb;
				rgb = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
				return ('#' + hex(rgb[1]) + hex(rgb[2]) + hex(rgb[3])).toUpperCase();
			}
			catch (ex) {
				return null;
			}
		},

		/**
		 * Remove the quotes from string
		 * Example: input : ""test"" output: "test"
		 * @param  {String} str
		 */
		removeQuotes: function(str) {
			return str.replace(/\"/g, "");
		},

		/**
		 * Select all text in text area(div) which has contentEditable true
		 */
		selectAllText: function() {
			document.execCommand('selectAll', false, null);
		},

		/**
		 * Return number of locked shapes
		 * @param  {_Shape[]} shapes
		 * @return {number}
		 */
		unlockedShapedCount: function(shapes) {
			var self = this;
			return _.reduce(shapes, function(memo, shape) {
				return memo + (self.isShapeLocked(shape) ? 0 : 1);
			}, 0);
		},

		/**
		 * Return true if shape is of type PICTURE / IMAGE
		 * @param {Shape} shape.
		 * @returns {boolean}
		 */
		isImageShape: function(shape) {
			return shape && shape.type === "PICTURE";
		},

		/**
		 * Return true if shape is locked.
		 * @param {Shape} shape.
		 * @returns {boolean}
		 */
		isShapeLocked: function(shape) {
			return shape && this.resolve("spPr.locked", shape);
		},

		zoomAttribute: function(/*Any*/v) {
			// summary: adjust style attribute based on zoom factor
			if (v && v.indexOf && v.indexOf(this._zoomPrefix) == 0) {
				return Number(v.substr(2)) * this.pixelToSvgRatio / this.zoomFactor;
			}

			return v;
		},

		resolve: function(path, obj) {
			return path ? path.split('.').reduce(function(prev, curr) {
				return prev ? prev[curr] : undefined
			}, obj) : undefined;
		},

		/**
		 * Return whether arrow key is pressed or not
		 * @param {number} keyCode
		 * @return {boolean}
		 */
		isArrowKeyPressed: function(keyCode) {
			return (keys.UP_ARROW === keyCode || keys.RIGHT_ARROW === keyCode
				|| keys.LEFT_ARROW === keyCode || keys.DOWN_ARROW === keyCode);
		},

		/**
		 * Convert given HTML content to a Blob containing PNG data
		 * @param {string} htmlContent - HTML content
		 * @param {number} scale - scale to apply to SVG content to match desired size of image
		 * @param {number} height - pixel height of image
		 * @param {number} width - pixel width of image
		 * @param {number} imageSizeLimit - limit to size of image (bytes)
		 * @returns {Blob}
		 */
		svgToBlob: async function(htmlContent, scale, height, width, imageSizeLimit) {
			const errorPrefix = "Rejecting Client Printing: ";

			// in case of safari or empty contents don't convert to base64.
			if (!htmlContent || !width || !height || sniff("safari") || sniff("ff")) {
				console.info(errorPrefix + "No Content/Width/Height or using Safari or Firefox");
				return;
			}

			// replace all <br> with <br /> otherwise XML parser throws error: mismatched tag
			// XML does not understand &nbsp; entity from Froala, so replace with corresponding entity
			const scaledHtml = htmlContent.replace(/style="/, 'style="transform-origin: left top; transform: scale(' + scale + '); ')
				.replace(/<br>/gi, "<br/>")
				.replace(/&nbsp;/g, "&#160;");

			// Get used font families.
			const usedFontFamilies = fontUtility.getFontFamiliesFromHTML(htmlContent);

			// Get font face and style rules.
			const {cssFontFaceRules, cssStyleRules} = await fontService.getCSSRules(true, true, usedFontFamilies);

			// wrap html in foreignObject
			const svgContent =
				`<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${width} ${height}" version="1.1">` +
				`<foreignObject height="100%" width="100%">` +
				`<style>` +
				// C8-27548: foreignObject changes font kerning and causes some word to wrap when they shouldn't, reset to normal.
				`foreignObject {
								font-family: Centric8;
								font-kerning: normal;
								font-size: 12px;
								font-weight: 400;
								line-height: unset;
							}` +
				`${cssStyleRules.join("\n")}` +
				`${cssFontFaceRules.join("\n")}` +
				`</style>` +
				`${scaledHtml}` +
				`</foreignObject>` +
				`</svg>`;

			// get SVG document
			svgDoc = util.newXMLDocument(svgContent);

			// get all image defs and convert to base64
			const images = svgDoc.querySelectorAll("defs image");
			// since chrome images now aren't working in foreignObject, any image just means we use pdf service.
			if (images.length) {
				return;
			}

			// Any rejection will cause Snapshot to be generated on the server via PDF Service.
			const canvas = document.createElement("canvas");
			canvas.height = height;
			canvas.width = width;

			return await new Promise((resolve, reject) => {
				const finish = (data) => {
					resolve(data);
				}

				const img = document.createElement("img");
				img.onload = () => {
					canvas.getContext("2d").drawImage(img, 0, 0);

					try {
						return canvas.toBlob(finish);
					} catch (err) {
						console.info(errorPrefix + "Error while converting Canvas box to Blob.", err);
						reject();
					}
				};
				img.onerror = (err) => {
					console.info(errorPrefix + "Error while loading SVG image.", err);
					reject();
				};
				try {
					img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(util.serializeXML(svgDoc))));
				} catch (err) {
					console.info(errorPrefix + "Error while parsing serialized xml to base64.", err);
					reject();
				}
			});
		},

		/**
		 * Convert key to display string and return it
		 * @param {string} key - the node key
		 */
		getDisplayNameForKey: function(key) {
			const keyInstance = new Key({dpkey: key});
			let displayName = key;

			if (keyInstance.id === reflection.ATTR_IMAGES) {
				if (!keyInstance.index) {
					// default image so return localized string
					displayName = locale.resource("Default");
				} else {
					const rc = keyInstance.extendedPath.getEndClass();
					const imageLabelEnum = rc.imageLabelEnum;
					const enumKey = xml.enumCreate(imageLabelEnum.getId(), keyInstance.index);
					displayName = locale.getEnumName(enumKey);
				}
			} else {
				const specBuilder = new SpecBuilder();
				const spec = specBuilder.getSpecForKey(keyInstance);
				if (spec) {
					displayName = spec.name;
				} else if (keyInstance.id) {
					displayName = siteDefaultLocale.getTypeAttribute(keyInstance.extendedPath.getEndType(), keyInstance.id);
				} else if (keyInstance.prop == xml.PROP_NAME) {
					displayName = siteDefaultLocale.getTypeName(keyInstance.extendedPath.getEndType());
				} else if (keyInstance.prop) {
					displayName = siteDefaultLocale.getResource(keyInstance.prop);
				}
			}

			return displayName;
		},

		/**
		 * Get the localized type for a key
		 * @param {string} key
		 */
		getDisplayNameForType: function(key) {
			return locale.type(key);
		},

		/**
		 * Return the type for the provided key
		 * @param {string} key - the node key
		 */
		getStartTypeForKey: function(key) {
			var keyInstance = new Key({dpkey: key});
			return keyInstance.extendedPath ? keyInstance.extendedPath.startType : null;
		},

		/**
		 * Get the localized string for a key
		 * @param {string} key
		 */
		localizeString: function(key) {
			return locale.resource(key);
		},


		/**
		 * Edit an attribute placeholder
		 * @param {string[]} attributeKeys
		 * @param {any} groupObject
		 */
		displayCustomView: function(attributeKeys, groupObject) {
			// Consolidate information about previous attribute placeholder inside object
			var obj = {
				previousAttributeKeys: attributeKeys,
				groupObject: groupObject
			};
			// Pass this information on to the C8 function
			topic.publish("/csi/canvas/displayCustomView", obj);
		},

		/**
		 * Display type instances in canvas right-hand menu for an attribute placeholder type
		 * @param {string} type
		 */
		displayTypeInstances: function(type) {
			// Using dojo topic subscribe/publish allows for communication throughout c8
			topic.publish("/csi/canvas/displayTypeInstances", type);
			topic.publish("/csi/canvas/arrangeAccordionsByType", type);
		},

		/**
		 * Auto-fill attribute placeholders that can be auto-filled.
		 * @param {Node} canvasWidgetNode
		 * @param {string} canvasType - type to be compared with placeholder type
		 * @param {Object[]} pagesData - array of data for canvas pages
		 * @returns {Object[]} - array of pages data with auto-fill performed if possible
		 */
		autoFillAttributePlaceholders: function(canvasWidgetNode, canvasType, pagesData) {
			var view = canvasWidgetNode.view;

			// Do not perform auto-fill for Size Chart, Spec Data Sheet, Style BOM
			var rootNode = view.getViewNode();
			var rootClass = reflection.getNodeClass(rootNode);

			if (rootClass.isDerivedFrom("DataSheetRevision") && rootClass.getType() !== "PrintDesignRevision") {
				var masterNode = view.cacheView.get(rootNode.__Parent__);

				if (masterNode.__Parent__ === csi.EMPTY_URL) {
					// This is a data sheet template so don't autofill.
					return new Deferred().resolve(pagesData);
				}
			}

			if (pagesData && pagesData.length) {
				var self = this;
				var canvasApp = canvasWidgetNode.canvasApp;
				var placeholderData = [];
				var flatPlaceholderData = [];
				var nodeID = view.urlRoot;

				// Get the view context with the node set to urlRoot.
				var ctx = view.getContext(nodeID);

				// Gather data for all placeholders on all pages so we can get all dependencies.
				// Canvas is not yet rendered so processing using page ContentJson.
				_.each(pagesData, function(page) {
					if (page.ContentJson) {
						var contentDataJSON = JSON.parse(page.ContentJson);

						if (contentDataJSON.pageObjects) {
							// Attribute keys have not been assigned yet so these are the actual keys,
							// in ContentJson they will be indexes into these attributeKeys.
							var keys = page.Keys;

							// Get placeholder data for this page. Organized by group so flatten results.
							var pagePlaceholderObjectData = canvasApp.pageHelper.getAllPagePlaceholderObjectData(contentDataJSON);

							// Flatten data to be able to replace attribute keys with actual attribute keys.
							var flatPagePlaceholderData = self.flattenPlaceholderData(pagePlaceholderObjectData);

							// Replace attributeKey indexes with the actual attributeKeys.
							self.replaceAttributeKeyIndexes(flatPagePlaceholderData, keys);

							placeholderData = placeholderData.concat(placeholderData);

							// Store all flattened placeholder data for efficiency with some operations.
							flatPlaceholderData = flatPlaceholderData.concat(flatPagePlaceholderData);
						}
					}
				});

				// Ensure nodes populating placeholders are in cache.
				var nodesDfd = self.getPlaceholderDependencies(ctx, flatPlaceholderData, [ctx.getNodeURL()]);

				// This executes when placeholder dependencies have been placed in ctx cache.
				return when(nodesDfd, function() {
					// Need to loop again because we update page data via references.
					for (const page of pagesData) {
						// Grab the attributeKeys for this page.
						const keys = page.Keys;

						// setup placeholders for canvas instances created before C8-16890 7.2
						page.ContentJson = canvasApp.pageHelper.setupPageContentPlaceholderHTML(page.ContentJson);

						if (page.ContentJson) {
							const contentDataJSON = JSON.parse(page.ContentJson);
							if (contentDataJSON.pageObjects) {
								for (const po of contentDataJSON.pageObjects) {
									// Get placeholders on this page.
									const pagePlaceholderObjects = canvasApp.pageHelper.getPagePlaceholderObjects(po) || [];

									if (pagePlaceholderObjects.length) {
										for (const placeholder of pagePlaceholderObjects) {
											// Placeholder attributeKey could be an index, get the real key.
											const attributeKey = keys[placeholder.attributeKey] || placeholder.attributeKey;

											// If a real key was found, set the placeholder key to the real key
											if (attributeKey) {
												placeholder.attributeKey = attributeKey;
											}

											// Only do auto-fill if placeholder type is same as canvas type.
											if (self.getStartTypeForKey(attributeKey) == canvasType) {
												// Get the latest placeholder value for auto-fill.
												const latestData = self.getPlaceholderValue(ctx, placeholder, nodeID);
												const {imageDimensions, value} = latestData;
												if (latestData.isImage && value) {
													const obj = {
														preserveAspectRatio: "xMinYMin meet",
														scale: self.pixelToSvgRatio,
														height: imageDimensions.h,
														width: imageDimensions.w,
														// Don't need x and y because we will use the position where the image is added
														x: 0,
														y: 0,
														imageLink: value
													};

													// Need to explicitly change and delete objects in placeholder, as simply assigning to
													// it breaks the reference thus the placeholder data to be rendered will not be updated.
													const imagePlaceholderObject = canvasApp.pageHelper.makeImageFromPlaceholder(placeholder, obj);
													placeholder.isPictureFromPlaceholder = imagePlaceholderObject.isPictureFromPlaceholder;
													placeholder.isPicture = imagePlaceholderObject.isPicture;
													placeholder.nodeURL = imagePlaceholderObject.nodeURL;
													placeholder.spPr = imagePlaceholderObject.spPr;
													delete placeholder.imagePlaceholder;
													delete placeholder.txBody;
												} else if (latestData.isImage && !value) {
													restoreDefaultPlaceholderView(placeholder, true);
												} else {
													addAttributeToObject(placeholder, {
														format: placeholder.format,
														permission: true,
														text: value,
													});
												}
											}
										};
									}

									// if we have autospace with a placeholder, reset extents
									if (po.autospace) {
										canvasApp.pageApi().resetPlaceholderExtends(po);
									}
								};
								page.ContentJson = JSON.stringify(contentDataJSON);
							}
						}
					};
					return pagesData;
				});
			}
			// Always return something, even if pagesData was not defined.
			return new Deferred().resolve(pagesData);
		},

		/**
		 * Search through nested groups to assure a placeholder is filled and showLabel is on
		 * @param {PageObjectTypes[]} pageObjects - page objects to iterate over
		 * @return {Object} containing filled and showLabel booleans
		 */
		placeholderFilledAndShowLabel: function(pageObjects) {
			let filled = true;
			let showLabel = true;
			const _resursive = (pageObjects) => {
				pageObjects.forEach((pageObject) => {
					if (pageObject.childObjects) {
						return _resursive(pageObject.childObjects);
					}
					if (!pageObject.isTextShape) {
						return;
					}
					if (filled && (!pageObject.filled || !pageObject.attributeKey)) {
						filled = false;
					}
					if (showLabel && !pageObject.showLabel) {
						showLabel = false;
					}
				});
			}
			_resursive(pageObjects);
			return {filled, showLabel};
		},

		/**
		 * Return true if the attribute needs a label
		 * @param {AttributeSpec} spec - specification for the attribute
		 * @returns {boolean}
		 */
		specNeedsLabel: function(spec) {
			// TODO : Add conditions for whether spec needs label
			return true;
		},

		/**
		 * Gets the appropriate text for the attribute, or gives an error message
		 * @param {Context} ctx - context to use for the attribute
		 * @param {Spec} spec - attribute spec
		 * @returns {String}
		 */
		getAttributeText: function(ctx, spec) {
			let resultString;
			try {
				resultString = csiHeading.toText(ctx, spec.heading);
				// support for multiline comments in canvas
				if (resultString !== "" &&
					((spec.format === types.FORMAT_COMMENT) ||
						(spec.format === types.FORMAT_COMMENT_LATEST))) {
					const json = JSON.parse(resultString);
					const data = json.data[0];
					if (data) {
						const date = types.formatTime(data.date, types.FORMAT_DATE);
						resultString = `${data.author} ${date}: ${data.value}`;
					} else {
						resultString = "";
					}
				}

			} catch (err) {
				resultString = `<b><font color="red">${locale.resource("ERROR")}</font></b>`;
			}

			return resultString;
		},

		/**
		 * Get the latest attribute placeholder value.
		 * @param {Context} ctx - context for the view
		 * @param {Object} placeholder - placeholder object
		 * @param {string} nodeID - id for the node from which placeholder gets data
		 * @returns {Object} - latest attribute value and flag true if is image
		 */
		getPlaceholderValue: function(ctx, placeholder, nodeID) {
			// Get the node associated with the attribute to get the latest value.
			const node = ctx.cache.getNode(nodeID);
			const latestValueData = {value: "", isImage: false, imageDimensions: null, name: ""};

			if (node) {
				ctx.setNode(node);
				ctx.bPathsQueried = true;
				const key = new Key({dpkey: placeholder.attributeKey});
				const specs = csiCard.getAttributeCardSpecs(node.$Type, [key]);
				const attributeSpec = specs.attributeSpecs[0];
				if (!attributeSpec) {
					return latestValueData;
				}
				latestValueData.name = attributeSpec.name;
				placeholder.format = attributeSpec.format;

				let resultString = this.getAttributeText(ctx, attributeSpec);

				if (attributeSpec.format === types.FORMAT_IMAGE) {
					latestValueData.isImage = true;
					if (!ctx.pathUnreachable) {
						const imageDimensions = {};
						resultString = csiHeading.toImageViewable(ctx, attributeSpec.heading, imageDimensions) || "";
						latestValueData.imageDimensions = imageDimensions;
					}
				}

				if (ctx.pathUnreachable) {
					resultString = locale.resource("CanvasNoPermission");
				}

				latestValueData.value = resultString;
			} else {
				// If we can't find the node, it could have just been deleted, in which case we still want to set
				// whether or not the deleted placeholder is an image placeholder (for use later in ImageReviewWidget.js).
				latestValueData.isImage = placeholder.imagePlaceholder;
			}

			return latestValueData;
		},

		/** Ensure the cache has the nodes that populate attribute placeholders
		 * @param {Context} ctx - context for the view
		 * @param {Object[]} pagesContent - content for all canvas pages
		 * @param {[string]} urls - urls to add to placeholder data
		 * @return {Deferred}
		 */
		getPlaceholderDependencies: function(ctx, placeholderData, urls) {
			let paths = [];

			// Collect the unique urls and keys.
			urls = ((urls || []).concat(_.uniq(_.pluck(placeholderData, "nodeURL")))).filter(url => url);

			const keys = _.uniq(_.pluck(placeholderData, "attributeKey")).filter(key => key);

			// get paths
			keys.forEach((key) => {
				key = new Key({dpkey: key});
				const specs = csiCard.getAttributeCardSpecs(key.extendedPath.startType, [key]);
				paths = paths.concat(csiCard.getPathsForSpecs(specs));
			});

			// Get the paths for the specs.
			if (urls.length) {
				paths = _.uniq(paths);
				// Now get the node dependencies into the cache.
				return ctx.cache.getNodeDependencies(urls, paths, false);
			}
		},

		/** Add a flag to placeholder attributes that the placeholder should be deleted
		 * @param {Object[]} placeholderData - data for placeholders
		 */
		addDeletePlaceholderFlag: function(placeholderData) {
			var self = this;
			var keysForTypes = {};
			var specs = [];

			// Get all attributeKeys, used to get all the attributeKey specs.
			// Specs are used to verify attributes are in system and enabled.
			var keys = _.uniq(_.pluck(placeholderData, "attributeKey"));

			// Organize the keys by type.
			_.each(keys, function(key) {
				var type = self.getStartTypeForKey(key);
				// Creates intermediate objects if necessary.
				lang.setObject(type + "." + key, "", keysForTypes);
			});

			// Get specs for attribute keys, used to see if attributes exist and are enabled.
			_.each(keysForTypes, function(keys, type) {
				// Keys for a type stored as object keys to avoid duplicates.
				var keysData = _.keys(keys);

				keysData = keysData.map(function(key) {
					return new Key({dpkey: key});
				});

				var fullSpecs = csiCard.getAttributeCardSpecs(type, keysData);
				specs = specs.concat(fullSpecs.attributeSpecs);
			});

			var specsMap = {};
			_.each(specs, function(spec) {
				var specInvalid = spec.ra && !spec.ra.isEnabled;
				if (specInvalid == undefined) {
					specInvalid = false;
				}
				var specAttributeKey = spec.getKey();
				specsMap[specAttributeKey] = specInvalid;
			});

			_.each(placeholderData, function(placeholder) {
				// sometimes, the image key can have a slash in it e.g. "Images/:(Style):0" instead of "Images:(Style):0"
				// so convert it to a key, then get it from the specsMap
				const key = Key.toKey(placeholder.attributeKey);
				var specInvalid = specsMap[key.typedString];
				placeholder.deletePlaceholder = (specInvalid == undefined) || specInvalid;
			});
		},

		/** Extract placeholders from data structure.
		 * @param {Object[]} placeholderData - data for placeholders in this structure:
		 *    top-level array
		 *        page index
		 *            group shape ids (groups in page)
		 *                array of placeholders in group
		 */
		flattenPlaceholderData: function(placeholderData) {
			var flatData = [];

			_.each(placeholderData, function(pageData) {
				flatData = flatData.concat(_.flatten(_.values(pageData)));
			});

			return flatData;
		},

		/** Replace numbered attribute key indexes with actual attribute keys.
		 * @param {Object[]} placeholderData - data for placeholders
		 */
		replaceAttributeKeyIndexes: function(placeholderObjectData, keys) {
			_.each(placeholderObjectData, function(data) {
				data.attributeKey = keys[data.attributeKey];
			});

		},

		/** Add IDs for efficient processing to the placeholders
		 * @param {Object[]} placeholderData - data for placeholders
		 */
		addPlaceholderIds: function(placeholderData) {
			_.each(placeholderData, function(pageData, index) {
				// Go though placeholder groups on each page.
				_.each(pageData, function(placeholderGroup, groupShapeId) {
					// Check each attribute placeholder in the group.
					_.each(placeholderGroup, function(placeholder) {
						// Add page index and group shape id to placeholders for more efficient processing.
						placeholder.pageIndex = index;
						placeholder.groupShapeId = groupShapeId;
					});
				});
			});
		},

		/** Filter to only include placeholder attributes that have changed or should be deleted.
		 * @param {Object[]} placeholderData - data for placeholders
		 * @param {Object[]} deletions - stores placeholders to be deleted
		 */
		filterAttributesForChangeOrDeletion: function(placeholderData, deletions) {
			var attributesForChangeOrDeletion = [];

			// Go through placeholder attributes and see which are changed or should be deleted.
			_.each(placeholderData, function(pageData, index) {
				var changeDeletePageData = {};

				// Go though placeholder groups on each page.
				_.each(pageData, function(placeholderGroup, groupShapeId) {
					// Check each attribute placeholder in the group.
					_.each(placeholderGroup, function(placeholder) {
						// Keep placeholders whose "changed" or "deletePlaceholder" flags are true.
						// Keep all data in groups with at least one changed placeholder.
						if (placeholder.changed && !changeDeletePageData[groupShapeId]) {
							changeDeletePageData[groupShapeId] = placeholderGroup;
						}
						if (placeholder.deletePlaceholder && !_.contains(deletions, placeholder.attributeKey)) {
							deletions.push(placeholder.attributeKey);
						}
					});
				});
				attributesForChangeOrDeletion.push(changeDeletePageData);
			});
			return attributesForChangeOrDeletion;
		},

		/**
		 * Check if string has URL query parameter URL=
		 * @param {string} target
		 * @returns {boolean} True if target has URL= query parameter
		 */
		hasURLQueryParameter: function(target) {
			return target.match && target.match(/.*?URL=.*/);
		},

		/**
		 * Show Max Canvas Pages error message
		 */
		popupMaxPageErrorMessage: function() {
			message.errorDialog({
				materialIcon: MessageDialog.ICON_WARNING,
				message: locale.resource("CanvasAddPageErrorMessage"),
				title: locale.resource("CanvasAddPageErrorTitle", [api.getCanvasPageLimit()])
			});
		}
	};
});
